import { Formik, FormikProps } from "formik";
import { isEqual } from "lodash";
import { ChangeEvent, useRef, useState } from "react";
import { useMutation } from "react-query";
import AGButton from "../../../components/Button";
import AGInput from "../../../components/Input";
import AGModal from "../../../components/Modal";
import { useNotification } from "../../../components/Notification";
import Textarea from "../../../components/Textarea";
import { FirmProfile } from "../../../models";
import { videoSchema } from "../../../Schema/CommerceCompassSchema";
import { httpPUT } from "../../../utils/ajaxHelper";
import { FormContainer } from "./cardDescriptionModal.styled";

export interface AddProchureModalProps {
	visible: boolean;
	toggleModal: () => void;
	data?: FirmProfile;
	isHeightMobile: boolean;
}
const AddDescriptionModal = ({
	visible,
	toggleModal,
	data,
	isHeightMobile,
}: AddProchureModalProps) => {
	const handleClose = () => {
		toggleModal();
	};

	const formRef = useRef<FormikProps<any>>(null);
	const { showNotification } = useNotification();
	const [isDataChanged,setIsDataChanged] = useState<boolean>(false);

	const { mutate: submitForm } = useMutation(
		(data: FormData) =>
			httpPUT({
				url: "/FirmProfile",
				data: data,
			}),
		{
			onSuccess: (rs) => {
				handleClose();
			},
			onError: (rs) => {
				showNotification("Upload Failed", "Error");
			},
		},
	);

	if (!data) {
		return null;
	}

	const intitialValues = {
		fkFirmId: data!.fkFirmId,
		logoName: data!.logoName,
		logoPart: data!.logoPart,
		backgroundName: data!.backgroundName,
		backgroundPart: data!.backgroundPart,
		description: data!.description,
		tagLine: data!.tagLine,
		offerorURL: data!.offerorURL,
		videoUrl: data!.videoUrl,
		brochureName: data!.brochureName,
		brochurePath: data!.brochurePath,
	}
	
	return (
		<AGModal
			toggleModal={toggleModal}
			title="Organization Description"
			footer={
				<>
					<AGButton
						type="Primary"
						onClick={() => {
							formRef.current?.submitForm();
						}}
						disabled={!isDataChanged}
						>
						Save
					</AGButton>
				</>
			}
			visible={visible}
			setHeight={isHeightMobile ? undefined : 560}>
			<FormContainer>
				<Formik
					innerRef={formRef}
					initialValues={
						{
							fkFirmId: data!.fkFirmId,
							logoName: data!.logoName,
							logoPart: data!.logoPart,
							backgroundName: data!.backgroundName,
							backgroundPart: data!.backgroundPart,
							description: data!.description,
							tagLine: data!.tagLine,
							offerorURL: data!.offerorURL,
							videoUrl: data!.videoUrl,
							brochureName: data!.brochureName,
							brochurePath: data!.brochurePath,
						} as any
					}
					validateOnBlur={false}
					validationSchema={videoSchema}
					onSubmit={(values) => {
						const formData = new FormData();
						formData.append("fkFirmId", values.fkFirmId);
						formData.append(
							"description",
							values.description ? values.description?.trim() : "",
						);
						formData.append(
							"tagLine",
							values.tagLine ? values.tagLine?.trim() : "",
						);
						formData.append(
							"videoUrl",
							values.videoUrl ? values.videoUrl?.trim() : "",
						);
						formData.append(
							"offerorURL",
							values.offerorURL ? values.offerorURL?.trim() : "",
						);
						submitForm(formData);
					}}>
					{({ values, errors, setFieldValue, submitForm }) => {

						const isDirty = !isEqual(
							{ ...values
							},
							intitialValues
						);

						setIsDataChanged(isDirty);

						const hasOfferorURLError =
						typeof errors.offerorURL === 'string' ? errors.offerorURL.length > 0 :
						Array.isArray(errors.offerorURL) ? errors.offerorURL.length > 0 :
						false;

						setIsDataChanged(hasOfferorURLError ? false : isDirty);

						return (
							<form onSubmit={(e) => e.preventDefault()} className="form">
								<div className="form-content">
									<div>
										<div className="label-container">
											<label className="label-title text-[14px] text-[#58585B]">
												Tag Line:
											</label>
											<label className="label-title-second">
												<br />A brief phrase that
												creates a lasting impression
												about your brand’s value.
											</label>
										</div>
										
										<div className="custom-select">
											<AGInput
												className="custom-input w-full text-[14px]"
												onChange={(
													e: ChangeEvent<HTMLInputElement>,
												) => {
													setFieldValue(
														"tagLine",
														e.target.value,
													);
													if (e.target.value.trim() === "") {
														setFieldValue("tagLine",null,);
													}
												}}
												value={values.tagLine}
												placeholder="This is where the organization tagline would go"
											/>
										</div>
									</div>
									<div>
										<div className="label-container">
											<label className="label-title text-[14px] text-[#58585B]">
												Organization URL:
											</label>
											<label className="label-title-second">
												<br />Provide URL of your organization for this submission.
											</label>
										</div>
										
										<div className="custom-select">
											<AGInput
												className="offerorURL custom-input w-full text-[14px]"
												onChange={(
													e: ChangeEvent<HTMLInputElement>,
												) => {
													setFieldValue("offerorURL",e.target.value,);
													if (e.target.value.trim() === "") {
														setFieldValue("offerorURL",null,);
													}
												}}
												value={values.offerorURL}
												placeholder="Enter URL here"
											/>
											{errors.offerorURL && (
												<div className="error">
													{errors.offerorURL.toString()}
												</div>
											)}
										</div>
									</div>
									<div className="form-content">
										<div>
											<label className="label-title text-[14px] text-[#58585B]">
												Description:
											</label>
											<label
												className="label-title-second"
												htmlFor="">
												<br />
												Provide a concise description of
												your organization for this submission.
											</label>
											<div className="custom-select">
												<Textarea
													placeholder="Enter description here"
													className="text-area"
													value={values.description}
													maxLength={2000}
													onChange={(
														e: ChangeEvent<HTMLTextAreaElement>,
													) => {
														setFieldValue(
															"description",
															e.target.value,
														);
														if (e.target.value.trim() === "") {
															setFieldValue("description",null,);
														}
													}}
												/>
											</div>
										</div>
									</div>
								</div>
							</form>
						);
					}}
				</Formik>
			</FormContainer>
		</AGModal>
	);
};
export default AddDescriptionModal;
